import React, { useState, useEffect } from 'react';
import { FaClock, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import StartSidebar from '../components/StartSidebar';

const StartHourly = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const isStartContext = location.pathname.startsWith('/start/');
  const [hourlyRates, setHourlyRates] = useState({});
  const [hourPrices, setHourPrices] = useState({
    perHour: 0,
    5: 0,
    10: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load data from Firestore on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load hourly rates data
        const ratesSnapshot = await getDocs(collection(db, 'hourlyRates'));
        if (!ratesSnapshot.empty) {
          const ratesData = {};
          ratesSnapshot.forEach(doc => {
            ratesData[doc.id] = doc.data();
          });
          setHourlyRates(ratesData);
        }

        // Load hour prices from hoursTemplate
        const hoursTemplateDoc = await getDoc(doc(db, 'hoursTemplate', 'pricing'));
        if (hoursTemplateDoc.exists()) {
          const data = hoursTemplateDoc.data();
          
          // The data structure should have hourPrices object
          if (data.hourPrices) {
            setHourPrices({
              perHour: data.hourPrices.perHour || 0,
              5: data.hourPrices[5] || 0,
              10: data.hourPrices[10] || 0
            });
          } else {
            // Keep default values
            setHourPrices({
              perHour: 0,
              5: 0,
              10: 0
            });
          }
        } else {
          // Keep default values
          setHourPrices({
            perHour: 0,
            5: 0,
            10: 0
          });
        }

        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);





  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Row */}
        <div className="flex items-center justify-center mb-8 relative">
          {/* Back Button */}
          <button
            onClick={() => navigate('/start/program')}
            className="absolute left-0 w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FaArrowLeft className="text-lg" />
          </button>
          
          {/* Page Title */}
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            Hourly Services
          </h1>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
              Loading hourly services...
            </div>
          </div>
        )}


        {/* Content - Only show when not loading */}
        {!isLoading && (
          <>
            {/* Hourly Support Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Hourly Support Card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-4">
                    <FaClock className="text-4xl text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Hourly Support</h3>
                  <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
                    ${hourPrices.perHour || 0}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">per hour</p>
                  
                  <div className="text-left text-sm text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                    <p>• Billed at the end of the month for hours accrued</p>
                    <p>• Hours do not roll over month to month (use-it-or-lose-it)</p>
                    <p>• You'll have access to time tracking inside the client portal for full transparency</p>
                    <p>• You'll have the flexibility to allocate hours wherever they bring the most value</p>
                    <p>• Hours may be meeting time or task time</p>
                  </div>
                  
                  <button
                    onClick={() => navigate('/start/checkout', {
                      state: {
                        selectedProgram: {
                          name: 'Per Hourly',
                          pricePerMonth: hourPrices.perHour,
                          description: 'Hourly Support - Billed at the end of the month for hours accrued'
                        },
                        selectedTier: 'hourly',
                        billingCycle: 'monthly',
                        completedStep: 'hourly'
                      }
                    })}
                    className="w-full bg-yellow-500 text-white py-2 px-4 rounded-lg hover:bg-yellow-600 transition-colors font-semibold"
                  >
                    Choose Hourly
                  </button>
                </div>
              </div>

              {/* 5 Hours Card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-4xl text-blue-600 dark:text-blue-400">⏰</div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">5 Hours</h3>
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                    ${hourPrices[5] || 0}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">per month</p>
                  
                  <div className="text-left text-sm text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                    <p>• Hours do not roll over month to month (use-it-or-lose-it)</p>
                    <p>• Additional time beyond your package is available at $250/hr</p>
                    <p>• You'll have access to time tracking inside the client portal for full transparency</p>
                    <p>• You'll have the flexibility to allocate hours wherever they bring the most value</p>
                    <p>• Hours may be meeting time or task time</p>
                  </div>
                  
                  <button
                    onClick={() => navigate('/start/checkout', {
                      state: {
                        selectedProgram: {
                          name: '5 Hours',
                          pricePerMonth: hourPrices[5],
                          description: '5 Hours Package - Monthly recurring'
                        },
                        selectedTier: '5hours',
                        billingCycle: 'monthly',
                        completedStep: 'hourly'
                      }
                    })}
                    className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors font-semibold"
                  >
                    Choose 5 Hours
                  </button>
                </div>
              </div>

              {/* 10 Hours Card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-4xl text-green-600 dark:text-green-400">💼</div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">10 Hours</h3>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                    ${hourPrices[10] || 0}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">per month</p>
                  
                  <div className="text-left text-sm text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                    <p>• Hours do not roll over month to month (use-it-or-lose-it)</p>
                    <p>• Additional time beyond your package is available at $250/hr</p>
                    <p>• You'll have access to time tracking inside the client portal for full transparency</p>
                    <p>• You'll have the flexibility to allocate hours wherever they bring the most value</p>
                    <p>• Hours may be meeting time or task time</p>
                  </div>
                  
                  <button
                    onClick={() => navigate('/start/checkout', {
                      state: {
                        selectedProgram: {
                          name: '10 Hours',
                          pricePerMonth: hourPrices[10],
                          description: '10 Hours Package - Monthly recurring'
                        },
                        selectedTier: '10hours',
                        billingCycle: 'monthly',
                        completedStep: 'hourly'
                      }
                    })}
                    className="w-full bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors font-semibold"
                  >
                    Choose 10 Hours
                  </button>
                </div>
              </div>
            </div>


          </>
        )}
      </div>

    </div>
  );
};

export default StartHourly;
